(() => {
  const STORAGE_KEY = 'trove:wishlist:v1';
  const UPDATE_EVENT = 'wishlist:updated';

  const readWishlist = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, {
        detail: {
          items,
          count: items.length,
        },
      })
    );
  };

  const normalizeProduct = (element) => ({
    id: String(element.dataset.productId || ''),
    handle: element.dataset.productHandle || '',
    title: element.dataset.productTitle || '',
    url: element.dataset.productUrl || '',
  });

  const hasItem = (items, product) =>
    items.some((item) => {
      if (product.id && item.id) return String(item.id) === String(product.id);
      return item.handle === product.handle;
    });

  const addItem = (product) => {
    if (!product.handle) return;

    const items = readWishlist();
    if (!hasItem(items, product)) {
      writeWishlist([...items, product]);
    }
  };

  const removeItem = (product) => {
    const items = readWishlist().filter((item) => {
      if (product.id && item.id) return String(item.id) !== String(product.id);
      return item.handle !== product.handle;
    });

    writeWishlist(items);
  };

  const formatMoney = (amount, currency) => {
    const value = Number(amount || 0) / 100;
    const shopify = window.Shopify || {};
    const activeCurrency = currency || shopify.currency?.active || 'JPY';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || shopify.locale || 'ja', {
        style: 'currency',
        currency: activeCurrency,
        maximumFractionDigits: activeCurrency === 'JPY' ? 0 : 2,
      }).format(value);
    } catch (error) {
      return `${Math.round(value).toLocaleString()} ${activeCurrency}`.trim();
    }
  };

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.update(readWishlist());
      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
    }

    disconnectedCallback() {
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }

    handleEvent(event) {
      if (event.type === 'storage' && event.key !== STORAGE_KEY) return;
      this.update(event.detail?.items || readWishlist());
    }

    update(items) {
      const count = items.length;
      this.hidden = count === 0;
      this.textContent = count > 99 ? '99+' : String(count);

      const template = count === 1 ? this.dataset.labelOne : this.dataset.labelOther;
      if (template) {
        this.setAttribute('aria-label', template.replace('[count]', count));
      }
    }
  }

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-wishlist-label]');
      this.product = normalizeProduct(this);

      if (!this.product.handle || !this.button) return;

      this.button.addEventListener('click', this);
      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
      this.sync(readWishlist());
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this);
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }

    handleEvent(event) {
      if (event.type === 'click') {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
        return;
      }

      if (event.type === 'storage' && event.key !== STORAGE_KEY) return;
      this.sync(event.detail?.items || readWishlist());
    }

    toggle() {
      const items = readWishlist();
      if (hasItem(items, this.product)) {
        removeItem(this.product);
      } else {
        addItem(this.product);
      }
    }

    sync(items) {
      const active = hasItem(items, this.product);
      const label = active ? this.dataset.removeLabel : this.dataset.addLabel;

      this.toggleAttribute('active', active);
      this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (label) {
        this.button?.setAttribute('aria-label', label);
        if (this.label) this.label.textContent = label;
      }
    }
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.toolbar = this.querySelector('[data-wishlist-toolbar]');
      this.countNumber = this.querySelector('[data-wishlist-count-number]');
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'JPY';

      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }

    handleEvent(event) {
      if (event.type === 'storage' && event.key !== STORAGE_KEY) return;
      this.render();
    }

    async render() {
      const items = readWishlist();

      if (!this.grid) return;

      this.grid.innerHTML = '';
      this.toolbar?.toggleAttribute('hidden', items.length === 0);
      if (this.countNumber) this.countNumber.textContent = String(items.length);
      this.empty?.toggleAttribute('hidden', items.length > 0);
      this.loading?.toggleAttribute('hidden', items.length === 0);

      if (items.length === 0) return;

      const products = await Promise.all(
        items.map(async (item) => {
          try {
            const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`);
            if (!response.ok) throw new Error('Product not found');
            return response.json();
          } catch (error) {
            return null;
          }
        })
      );

      this.loading?.setAttribute('hidden', '');

      const validProducts = products.filter(Boolean);
      this.empty?.toggleAttribute('hidden', validProducts.length > 0);

      validProducts.forEach((product) => {
        this.grid.insertAdjacentHTML('beforeend', this.renderCard(product));
      });
    }

    renderCard(product) {
      const productUrl = product.url || `/products/${product.handle}`;
      const image = product.featured_image || product.images?.[0] || '';
      const imageMarkup = image
        ? `<img src="${image}" alt="${this.escape(product.title)}" loading="lazy">`
        : '<div class="wishlist-page__placeholder"></div>';
      const compareMarkup =
        product.compare_at_price > product.price
          ? `<s>${formatMoney(product.compare_at_price, this.currency)}</s>`
          : '';

      return `
        <article class="wishlist-page__card">
          <div class="wishlist-page__media">
            <a href="${productUrl}" class="wishlist-page__image" aria-label="${this.escape(product.title)}">
              ${imageMarkup}
            </a>
            <wishlist-toggle
              class="wishlist-page__remove-wrap"
              data-product-id="${product.id}"
              data-product-handle="${this.escape(product.handle)}"
              data-product-title="${this.escape(product.title)}"
              data-product-url="${productUrl}"
              data-add-label="${this.escape(this.dataset.addLabel || 'Add to wishlist')}"
              data-remove-label="${this.escape(this.dataset.removeLabel || 'Remove from wishlist')}"
            >
              <button type="button" class="wishlist-page__remove-button" aria-pressed="true" aria-label="${this.escape(this.dataset.removeLabel || 'Remove from wishlist')}">
                <span aria-hidden="true">×</span>
                <span data-wishlist-label class="visually-hidden">${this.escape(this.dataset.removeLabel || 'Remove from wishlist')}</span>
              </button>
            </wishlist-toggle>
            <a href="${productUrl}" class="wishlist-page__quick-link" aria-label="${this.escape(this.dataset.viewLabel || 'View details')}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm10 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM4 4h2l2.2 10.2a1.8 1.8 0 0 0 1.8 1.4h6.9a1.8 1.8 0 0 0 1.7-1.2L21 7H7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </a>
          </div>
          <div class="wishlist-page__content">
            <h2 class="wishlist-page__title">
              <a href="${productUrl}" class="reversed-link">${this.escape(product.title)}</a>
            </h2>
            <p class="wishlist-page__price">
              <span>${formatMoney(product.price, this.currency)}</span>
              ${compareMarkup}
            </p>
          </div>
        </article>
      `;
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }

  if (!customElements.get('wishlist-toggle')) {
    customElements.define('wishlist-toggle', WishlistToggle);
  }

  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', WishlistPage);
  }
})();
